import dayjs from "dayjs";

/**
 * Extract description name from Transfer of Fund transaction
 */
export const extractFromTransferOfFund = (description2, description3) => {
  let descriptionName = null;
  let fullname = null;

  if (description2.substring(0, 3).toLowerCase() === "trf") {
    const parts = description2.split(" FT2");
    if (parts.length === 2) {
      let cleaned = parts[0].replace("TRF ", "");
      const splitDesc = cleaned.split(" ");

      const cleanedDesc = splitDesc.filter(
        (d) =>
          !d.toLowerCase().includes("tith") &&
          !d.toLowerCase().includes("offer") &&
          d !== "&",
      );

      if (
        cleanedDesc.length > 1 &&
        cleanedDesc[0].indexOf("-") === -1 &&
        cleanedDesc[0].length === 12
      ) {
        descriptionName = cleanedDesc[0];
      } else if (cleanedDesc.length > 1 && cleanedDesc[0].indexOf("-") > -1) {
        descriptionName = cleanedDesc[0]
          .split("-")
          .filter(
            (d) =>
              !d.toLowerCase().includes("tith") &&
              !d.toLowerCase().includes("offe"),
          )
          .join(" ");
      }
    }

    if (description2.includes("IB:")) {
      fullname = description2
        .split("IB:")[1]
        .split(" ")
        .slice(1)
        .join(" ")
        .split(" SGD ")[0];
    }
  } else {
    const parts = description2.split("TRF ");
    if (parts.length === 2) {
      const bankParts = parts[1].split(" I-BANK ");
      descriptionName = bankParts.length === 2 ? bankParts[0] : null;
    }
  }

  return { descriptionName, fullname };
};

/**
 * Extract description name from Paynow/Fast Payment
 */
export const extractFromPaynow = (
  description2,
  curYear,
  lastYear,
  nextYear,
) => {
  let descriptionName = null;
  let fullname = null;

  if (description2.includes("OTHER")) {
    const descSplit = description2.split("OTHER");
    const years = [
      { year: curYear, format: curYear.format("YYYY") },
      { year: lastYear, format: lastYear.format("YYYY") },
      { year: nextYear, format: nextYear.format("YYYY") },
    ];

    for (const { format } of years) {
      if (descSplit[1].includes(format)) {
        descriptionName = descSplit[1].split(format)[0];
        fullname = descriptionName;
        break;
      }
    }
  } else if (
    description2.includes("CHARITY PAYMENT") &&
    description2.includes("OCBCSGS")
  ) {
    const descSplit = description2.split("CHARITY PAYMENT ");
    if (descSplit[1].includes("OCBCSGS")) {
      descriptionName = descSplit[1]
        .split("OCBCSGS")[0]
        .split(" ")
        .slice(0, -1)
        .join(" ");

      fullname = descriptionName;
    }
  }

  return { descriptionName, fullname };
};

/**
 * Extract description name from Standing Order
 */
export const extractFromStandingOrder = (description2, description3) => {
  if (description2.includes("PART/REF") && description2.includes(" BY :")) {
    const descSplit = description2.split(" BY :");
    return descSplit[1].split(" PART/REF")[0];
  }

  const parts = description3.split(" SI  ");
  if (parts.length === 2) {
    const siParts = parts[0].split("SI  ");
    return siParts.length === 2 ? siParts[1] : null;
  }

  return null;
};

/**
 * Extract description name from Charity Payment
 */
export const extractFromCharityPayment = (description2, description3) => {
  if (
    description2.includes("CHARITY PAYMENT") &&
    description2.includes("OCBCSGS")
  ) {
    const descSplit = description2.split("CHARITY PAYMENT");
    if (descSplit[1].includes("OCBCSGS")) {
      return descSplit[1]
        .split("OCBCSGS")[0]
        .split(" ")[1]
        .pop()
        .join(" ");
    } else {
      return null;
    }
  }

  return null;
};

/**
 * Extract description name from generic payment types
 */
export const extractFromGenericPayment = (description2) => {
  if (description2.includes("GOVERNMENT PAYMENT")) {
    return null;
  }

  if (description2.includes(" Incoming PayNow From: ")) {
    return description2.toLowerCase().split(" incoming paynow from: ")[1];
  }

  const separators = [" other ", " charity payment ", " business expenses "];

  for (const separator of separators) {
    const parts = description2.toLowerCase().split(separator);
    if (parts.length === 2) {
      const sgdParts = parts[1].split(" sgd ");
      return sgdParts.length === 2 ? sgdParts[0] : null;
    }
  }

  return null;
};

/**
 * Clean and normalize description name
 */
export const cleanDescriptionName = (name, fullname) => {
  if (!name) return { descriptionName: null, fullname };

  let cleaned = name;

  if (!fullname) {
    fullname = name.toLowerCase().replace(/\s+/g, " ");
  }

  // Remove parts after special characters
  cleaned = cleaned
    .split(" (")[0]
    .split("/")[0]
    .split("@")[0];

  // Clean up spacing and remove paylah prefix
  cleaned = cleaned
    .toLowerCase()
    .replace("paylah! ", "")
    .replace(/\s+/g, " ");

  return { descriptionName: cleaned, fullname };
};

/**
 * Determine category from description or input mapping
 */
export const determineCategory = (
  descriptionName,
  description2,
  sortedInputs,
  sortedCategories,
) => {
  // Special category mappings
  const specialCategories = {
    interest: "INTEREST",
    iras: "IRAS",
    cash: "CASH",
    cheque: "CHEQUE",
  };

  const nameLower = descriptionName && descriptionName.toLowerCase();
  if (nameLower && specialCategories[nameLower]) {
    return specialCategories[nameLower];
  }

  // Find matching input
  const descLower = ` ${description2} `.toLowerCase();
  const matchedInput = sortedInputs.find((input) =>
    descLower.includes(input.title.toLowerCase()),
  );

  if (matchedInput) {
    const category = sortedCategories.find(
      (cat) => cat.id === matchedInput.category,
    );
    return category ? category.title.toUpperCase() : "TITHE";
  }

  return "TITHE";
};

/**
 * Find month remarks in description
 */
export const findMonthRemarks = (
  description,
  months,
  monthsCombo,
  yearCheckFn,
  curYear,
  lastYear,
  nextYear,
) => {
  const desc = ` ${description} `.toLowerCase();

  // Check for month combinations first
  for (const monthCombo of monthsCombo) {
    if (desc.includes(monthCombo.text.toLowerCase())) {
      return monthCombo.months;
    }
  }

  // Check for individual months with year
  return months
    .filter(
      (month) =>
        yearCheckFn(desc, month, curYear) ||
        yearCheckFn(desc, month, nextYear) ||
        yearCheckFn(desc, month, lastYear),
    )
    .map((month) => {
      if (yearCheckFn(desc, month, nextYear)) {
        return { month, year: nextYear.format("YY") };
      }
      if (yearCheckFn(desc, month, lastYear)) {
        return { month, year: lastYear.format("YY") };
      }
      return { month, year: curYear.format("YY") };
    });
};

/**
 * Format remark as YYYY-MM string
 */
export const formatRemark = (remark, date) => {
  if (remark && remark.month) {
    return dayjs(
      `01-${remark.month.substr(0, 3)}-${remark.year}`,
      "DD-MMM-YY",
    ).format("YYYY-MM");
  }

  if (remark) {
    return dayjs(
      `01-${remark.substr(0, 3)}-${dayjs().format("YY")}`,
      "DD-MMM-YY",
    ).format("YYYY-MM");
  }

  return dayjs(date, "DD-MMM-YYYY").format("YYYY-MM");
};

/**
 * Calculate leftover amount for first item in split
 */
export const calculateLeftover = (total, perItemAmount, itemCount) => {
  return total - perItemAmount * itemCount;
};

/**
 * Create data entry for bank statement
 */
export const createDataEntry = (params) => {
  const {
    category,
    date,
    amount,
    entity,
    remarks,
    cong,
    region,
    description2,
    workdate,
    week,
    dataLength,
  } = params;

  return {
    category,
    date: dayjs(date, "DD-MMM-YYYY").format("YYYY-MM-DD"),
    amount,
    entity: entity.replace(/\s+/g, " ").trim(),
    remarks,
    cong,
    region,
    description2,
    id: `IB${workdate.substr(2)}-${week * 10000 + dataLength}`,
    remarks2: "",
    status: "Received",
  };
};

/**
 * Add or update summary entry
 */
export const updateSummary = (summary, category, cong, region, amount) => {
  const existing = summary.find(
    (summ) =>
      summ.category === category &&
      summ.cong === cong &&
      summ.region === region,
  );

  if (!existing) {
    summary.push({ category, cong, region, total: 0 });
  }

  const index = summary.findIndex(
    (summ) =>
      summ.category === category &&
      summ.cong === cong &&
      summ.region === region,
  );

  summary[index].total += parseFloat(amount);
};
